import isEmpty from 'lodash/isEmpty';
import { emptyList, emptyObject } from '@constants/empty';
import { calculatorType } from '@constants/index';

export const getIsPendingAction = ({ quizTaking }) =>
  quizTaking.isPendingAction || false;

export const getIsQuizLoading = ({ quizTaking }) => quizTaking.isQuizLoading;

export const getQuizDetails =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking?.data?.[quizUID] || emptyObject;

export const getCurrentQuizType = ({ quizTaking }) =>
  quizTaking.currentQuizType || emptyObject;

export const getQuizAuthor =
  (quizUID) =>
  ({ quizTaking }) =>
    `${quizTaking.data[quizUID].author.first_name} ${quizTaking.data[quizUID].author.last_name}`;

export const getPreviousSessions =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.previous_sessions || emptyList;

export const getQuizSyllabus =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.syllabus || emptyList;

export const getHasDefaultSection =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID].sections.length === 1 &&
    quizTaking.data[quizUID].sections[0].is_default;

export const getdefaultSectionUID =
  (quizUID) =>
  ({ quizTaking }) =>
    getHasDefaultSection(quizUID)
      ? quizTaking.data[quizUID].sections[0].uid
      : null;

export const getQuizSessionDetails =
  (sessionUID) =>
  ({ quizTaking }) =>
    quizTaking.sessionDetailsByUID[sessionUID] || emptyObject;

export const getMinRankAPIDelay =
  () =>
  ({ quizTaking }) =>
    quizTaking.minRankAPIDelay;

export const getQuizSessionAnalytics =
  (sessionUID) =>
  ({ quizTaking }) =>
    quizTaking.analyticsReportByUID[sessionUID] || emptyObject;

export const getCurrentSession = ({ quizTaking }) => quizTaking.currentSession;

export const getQuizSection =
  (quizUID, index) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID].sections[index] || emptyObject;

export const getAllQuestionsBySection = ({ quizTaking }) =>
  quizTaking?.questionsBySection;

export const getAllAnswersBySection = ({ quizTaking }) =>
  quizTaking.answersBySection;

export const getAllMarkedQuestions = ({ quizTaking }) =>
  quizTaking.markedQuestionsBySection;

export const getUnansweredQuestions = ({ quizTaking }) =>
  quizTaking.unansweredQuestionsBySection ?? emptyObject;

export const getQuizSectionalQuestions =
  (quizUID, sectionUID) =>
  ({ quizTaking }) =>
    quizTaking.questionsBySection[sectionUID];

export const getAllSectionalAnswers =
  (sectionUID) =>
  ({ quizTaking }) =>
    quizTaking.answersBySection[sectionUID] || emptyObject;

export const getQuizQuestionAnswers =
  (sectionUID, questionUID) =>
  ({ quizTaking }) =>
    quizTaking.answersBySection[sectionUID]?.[questionUID] || emptyList;

export const getIsQuestionMarked =
  (sectionUID, quizUID) =>
  ({ quizTaking }) =>
    quizTaking.markedQuestionsBySection[sectionUID]?.[quizUID] || false;

export const getIsQuestionUnanswered =
  (sectionUID, quizUID) =>
  ({ quizTaking }) =>
    quizTaking?.unansweredQuestionsBySection?.[sectionUID]?.[quizUID] || false;

export const getCurrentSessionUID = ({ quizTaking }) =>
  quizTaking?.currentSession?.uid;

export const getLatestSessionUID =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.latest_session?.uid || null;

export const getCurrentQuestionInfo = ({ quizTaking }) =>
  quizTaking.currentQuestionInfo;

export const getCurrentSelectedAnswer = ({ quizTaking }) =>
  quizTaking.currentSelectedAnswer;

export const getQuestion =
  (sectionUID, questionUID) =>
  ({ quizTaking }) =>
    quizTaking.questionsBySection?.[sectionUID]?.byId?.[questionUID] ||
    emptyObject;

export const getCurrentQuestion = ({ quizTaking }) => {
  const { sectionUID, questionUID } =
    quizTaking.currentQuestionInfo || emptyObject;

  return (
    quizTaking.questionsBySection?.[sectionUID]?.byId?.[questionUID] ||
    emptyObject
  );
};

export const getSelectedLanguage = ({ quizTaking }) =>
  quizTaking.selectedLanguage;

export const getAvailableLanguages = ({ quizTaking }) =>
  quizTaking.availableLanguages;

export const getmaxAnswerableQuestions = ({ quizTaking }) =>
  quizTaking.maxAnswerableQuestionBySection;

export const getmaxAnswerableQuestion =
  (sectionUID) =>
  ({ quizTaking }) =>
    quizTaking.maxAnswerableQuestionBySection[sectionUID] || emptyObject;

export const getHasCalculator = ({ quizTaking }) =>
  !isEmpty(quizTaking.calculatorTypesBySection);

export const getCalculatorType =
  (sectionUID) =>
  ({ quizTaking }) =>
    quizTaking.calculatorTypesBySection[sectionUID] ||
    calculatorType.CALCULATOR_NOT_SELECTED;

export const getQuestionReportOptions = ({ quizTaking }) =>
  quizTaking.questionReportOptions;

export const getOngoingSectionDetails = ({ quizTaking }) =>
  quizTaking.ongoingSectionDetails;

export const getModalState = ({ quizTaking }) =>
  quizTaking.modal || emptyObject;

export const getSolutionFirstLevelFilters =
  (sessionUID) =>
  ({ quizTaking }) =>
    quizTaking.solutionFilterBySession[sessionUID]?.solutionFirstLevelFilters ||
    emptyList;

export const getSolutionSecondLevelFiltersByUID =
  (sessionUID) =>
  ({ quizTaking }) =>
    quizTaking.solutionFilterBySession[sessionUID]
      ?.solutionSecondLevelFiltersByUID || emptyObject;

export const getQuizSolutions =
  (key) =>
  ({ paginator }) =>
    paginator.quizSolutions[key];

export const getQuizSolutionFeedbackOptions = ({ quizTaking }) =>
  quizTaking.solutionFeedbackOptions;

export const getQuizVideoSolutionFeedbackOptions = ({ quizTaking }) =>
  quizTaking.videoSolutionFeedbackOptions;

export const getIsSolutionQuestionsaved =
  (questionUID) =>
  ({ quizTaking }) =>
    quizTaking.solutionSavedQuestions[questionUID];

export const getUserRank =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.rankByQuiz[quizUID]?.userRank ?? null;

export const getTotalSessionCount =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.rankByQuiz[quizUID]?.totalSessionCount ?? null;

export const getEvaluatedSheets = ({ quizTaking }) =>
  quizTaking.evaluatedSheetsBySesssion;

export const getEvaluatedSheet =
  (sessionUID) =>
  ({ quizTaking }) =>
    quizTaking.evaluatedSheetsBySesssion[sessionUID] || null;

export const getFeedbackOptions =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.feedbackOptionsByUID[quizUID] || emptyObject;

export const getIsQuizFeedbackGiven =
  (quizUID) =>
  ({ quizTaking }) => {
    if (isEmpty(quizTaking.feedbackOptionsByUID[quizUID])) {
      return true;
    }

    return !!quizTaking.feedbackOptionsByUID[quizUID].last_submitted_at;
  };

export const getLiveLeaderboard =
  (quizUID) =>
  ({ paginator }) =>
    paginator.quizLeaderboardLive[quizUID] || emptyObject;

export const getOverallLeaderboard =
  (quizUID) =>
  ({ paginator }) =>
    paginator.quizLeaderboardAll[quizUID] || emptyObject;

export const getLeaderboard = (quizUID, isLive) =>
  isLive ? getLiveLeaderboard(quizUID) : getOverallLeaderboard(quizUID);

export const getHasFetchedQuizQuestions = (quizUID) => (state) => {
  const { sections = {} } = getQuizDetails(quizUID)(state);
  const questionsBySection = getAllQuestionsBySection(state);
  const sectionsLength = sections.length;

  for (let i = 0; i < sectionsLength; i += 1) {
    if (!questionsBySection[sections[i].uid]) {
      return false;
    }
  }

  return true;
};

export const getIsQuizForPlusUsers =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.collection?.for_plus;

export const getIsUserEnrolledInQuiz =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.is_enrolled;

export const getGoalUId =
  (quizUID) =>
  ({ quizTaking }) =>
    quizTaking.data[quizUID]?.goal_uid ?? '';
